import { useNavigate, useRouterState } from '@tanstack/react-router';
import type { JSX } from 'react';

import type { AutomationSummaryProjection } from '@/lib/api/automations.js';
import { cn } from '@/lib/utils.js';

import {
  sidebarIconClass,
  sidebarRowActive,
  sidebarRowBase,
  sidebarRowHover,
} from '../../app/sidebar-primitives.js';

import { automationVisibilityIcon } from './automation-audience.js';
import { cronHumanize } from './cron-humanize.js';

/**
 * Sidebar row for an automation shared with the caller (`canManage: false`), not owned by them.
 * Opens the automation's dedicated chat — the shared object is the chat, not a read-only page —
 * and falls back to the read-only detail page when the automation carries no dedicated chat.
 * Read-only either way: no pencil, no menu, no Fire now.
 */
export function SharedAutomationRow({
  automation,
  onNavigate,
}: {
  automation: AutomationSummaryProjection;
  onNavigate?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const router = useRouterState();
  const name = automation.name?.trim() || 'Untitled';
  const Icon = automationVisibilityIcon(automation.visibility);
  const activeConvId = readSearchParam(
    router.location.search,
    'conversationId',
  );
  const active =
    automation.conversationId !== null &&
    activeConvId === automation.conversationId;

  function open(): void {
    if (automation.conversationId !== null) {
      void navigate({
        to: '/chats',
        search: { conversationId: automation.conversationId } as never,
      });
    } else {
      void navigate({ to: '/scheduled/$id', params: { id: automation.id } });
    }
    onNavigate?.();
  }

  return (
    <div
      data-visibility={automation.visibility}
      className={cn(
        sidebarRowBase,
        active ? sidebarRowActive : sidebarRowHover,
      )}
    >
      <button
        type="button"
        onClick={open}
        aria-label={name}
        aria-current={active ? 'page' : undefined}
        title={`${name} • Shared with you • ${cronHumanize(automation.schedule, automation.scheduleText)}`}
        className="flex min-w-0 flex-1 items-center gap-1.5 max-md:gap-3 text-left text-inherit"
      >
        <Icon className={sidebarIconClass} strokeWidth={1.75} aria-hidden />
        <span className="flex-1 truncate text-inherit">{name}</span>
      </button>
    </div>
  );
}

function readSearchParam(search: unknown, key: string): string | null {
  if (typeof search === 'string') {
    const sp = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    );
    return sp.get(key);
  }
  if (search !== null && typeof search === 'object' && key in search) {
    const v = (search as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : null;
  }
  return null;
}
